import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { TreePickerNode } from './TreePickerNode';
import './TreePicker.css';
import classNames from 'classnames';
import { ScrollView } from '../ScrollView/ScrollView';
import { TextInput } from '../TextInput/TextInput';
import { withTargetValue } from '../utils/events/withTargetValue';
import type { TreePickerItem } from './helpers/TreePickerItem';
import { countLeaves } from './helpers/countLeaves';
import { useTreePickerItems } from './useTreePickerItems';

const treePickerMsg = {
  searchPlaceholder: 'Search...',
  available: 'Available',
  selected: 'Selected',
};

export type TreePickerProps<TKey> = {
  items: TreePickerItem<TKey>[];
  value: Set<TKey>;
  onChange: (items: Set<TKey>) => void;
  disabled?: boolean;
  msg?: {
    availableTitle?: ReactNode;
    selectedTitle?: ReactNode;
  };
  collapsible?: boolean;
  showSelected?: boolean;
  showSearch?: boolean;
  showCounters?: boolean;
  noScroll?: boolean;
  noPadding?: boolean;
};

export const TreePicker = <TKey extends string>(
  props: TreePickerProps<TKey>,
): ReactNode => {
  const { availableItems, selectedItems, availableSearch, selectedSearch } =
    useTreePickerItems({ items: props.items, value: props.value });

  const itemsCount = useMemo(() => countLeaves(props.items), [props.items]);

  const {
    collapsible = true,
    showSelected = true,
    showSearch = true,
    showCounters = true,
  } = props;

  const renderTreePickerNodes = () =>
    availableItems.map((item) => (
      <TreePickerNode
        item={item}
        key={item.key}
        value={props.value}
        onChange={props.onChange}
        mode='available'
        disabled={props.disabled}
        expandedNodes={availableSearch.expandedNodes}
        collapsible={collapsible}
        showCounters={showCounters}
      />
    ));

  return (
    <div
      className={classNames(
        'tree-picker',
        showSelected && 'tree-picker--show-selected',
      )}
    >
      {!props.disabled && (
        <div
          className={classNames(
            'tree-picker__section mr-4',
            !props.noPadding && 'p-4',
          )}
        >
          {showCounters && (
            <div className='tree-picker__section-title'>
              {props.msg?.availableTitle ?? treePickerMsg.available}{' '}
              {`(${itemsCount})`}
            </div>
          )}
          {showSearch && (
            <TextInput
              className='mb-4'
              type='search'
              value={availableSearch.value}
              onChange={withTargetValue(availableSearch.handleChange)}
              placeholder={treePickerMsg.searchPlaceholder}
              onClear={() => {
                availableSearch.handleChange('');
              }}
            />
          )}
          {props.noScroll ? (
            renderTreePickerNodes()
          ) : (
            <ScrollView>{renderTreePickerNodes()}</ScrollView>
          )}
        </div>
      )}
      {showSelected && (
        <div
          className={classNames(
            'tree-picker__section',
            !props.disabled && 'ml-4',
            !props.noPadding && 'p-4',
          )}
        >
          {showCounters && (
            <div className='tree-picker__section-title'>
              {props.msg?.selectedTitle ?? treePickerMsg.selected}
              {' '}
              {`(${props.value.size})`}
            </div>
          )}
          {showSearch && (
            <TextInput
              className='mb-4'
              type='search'
              value={selectedSearch.value}
              onChange={withTargetValue(selectedSearch.handleChange)}
              placeholder={treePickerMsg.searchPlaceholder}
              onClear={() => {
                selectedSearch.handleChange('');
              }}
            />
          )}
          <ScrollView>
            {selectedItems.map((item) => (
              <TreePickerNode
                item={item}
                key={item.key}
                value={props.value}
                onChange={props.onChange}
                mode='selected'
                disabled={props.disabled}
                expandedNodes={selectedSearch.expandedNodes}
                collapsible={collapsible}
                showCounters={showCounters}
              />
            ))}
          </ScrollView>
        </div>
      )}
    </div>
  );
};
